import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingPopup from "../components/Popups/LoadingPopup/LoadingPopup.tsx";
import styles from "../components/Popups/Pricing/PricingFlow.module.scss";
import ConfirmationPopup from "../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import SelectOrganizationPopup from "../components/common/SelectOrganizationPopup/SelectOrganizationPopup.tsx";
import RouterConstants from "../constants/RouterConstants.ts";
import { ORG_SUBSCRIPTION_PLANS } from "../constants/billing.ts";
import {
	clearAwsRegistrationToken,
	getAwsRegistrationToken,
	getSelectedOrganization,
	setSelectedOrganization,
} from "../helpers/localStorage.ts";
import useSecureNavigate from "../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../hooks/useStores.tsx";
import { OrgSubscription } from "../models/billing.ts";
import { UserOrganization } from "../models/organization.ts";

const AwsChecker = () => {
	const { t } = useTranslation("translation", { keyPrefix: "account.awsUserSource.popup" });
	const { authStore, uiStore } = useAppStore();
	const [showAwsPlanConfirmationModal, setShowAwsPlanConfirmationModal] = useState(false);
	const [showSelectOrgModal, setShowSelectOrgModal] = useState(false);
	const [hasCheckedAws, setHasCheckedAws] = useState(false);
	const [loadingAwsAuth, setLoadingAwsAuth] = useState(false);
	const queryParams: URLSearchParams = new URLSearchParams(location.search);
	const selectedOrg = queryParams.get("orgId");
	const awsRegistrationToken = getAwsRegistrationToken();
	const navigate = useSecureNavigate();

	const getAwsPlanConfirmationMessage = () => {
		if (authStore.user && authStore.user.isAdminOrOwnerInTheSelectedOrganization()) {
			return authStore.orgSubscription?.plan_name === ORG_SUBSCRIPTION_PLANS.free.name
				? t("awsFreeOrgConfirmMessage")
				: t("awsOrgConfirmMessage");
		}
		return t("awsOrgAdminRequiredConfirmMessage");
	};

	const handleSelectOrg = async (selectedOrg: UserOrganization | null | undefined) => {
		if (!selectedOrg) return;
		queryParams.set("orgId", selectedOrg.organization.uid);
		window.history.replaceState(null, "", window.location.pathname + "?" + queryParams.toString());
		setSelectedOrganization(selectedOrg);
		window.location.reload();
	};

	const waitForSubscriptionReady = async () => {
		let subscription = authStore.orgSubscription;
		while (!subscription?.is_marketplace) {
			try {
				subscription = (await authStore.billingService.loadOrgSubscription()) as OrgSubscription;
			} catch (error) {
				console.error("API error:", error);
			}

			await new Promise((resolve) => setTimeout(resolve, 2000));
		}
		await authStore.loadOrgSubscription();
	};

	const awsAuthenticate = async () => {
		if (awsRegistrationToken) {
			setShowAwsPlanConfirmationModal(false);
			setLoadingAwsAuth(true);
			await authStore.awsAuthenticate(awsRegistrationToken);
			clearAwsRegistrationToken();
			await waitForSubscriptionReady();
			setLoadingAwsAuth(false);
			navigate(RouterConstants.MODELS.OVERVIEW.fullPath);
		}
	};

	const handleAwsPlanConfirmationConfirm = async () => {
		if (!authStore.user || !authStore.user.isAdminOrOwnerInTheSelectedOrganization() || !awsRegistrationToken) {
			setShowAwsPlanConfirmationModal(false);
			return;
		}

		await awsAuthenticate();
	};

	const checkAwsSource = async (): Promise<void> => {
		if (awsRegistrationToken) {
			if (authStore.userOrganizations.length === 0) {
				uiStore.showDialog("CreateOrganizationDialog");
			} else if (
				authStore.userOrganizations.length > 1 &&
				(!selectedOrg || selectedOrg !== getSelectedOrganization()?.organization.uid)
			) {
				setShowSelectOrgModal(true);
			} else {
				window.history.replaceState(null, "", window.location.pathname);
				const isOrgFromAws = await authStore.isOrgFromAWS();
				if (isOrgFromAws) {
					await awsAuthenticate();
				} else {
					setShowAwsPlanConfirmationModal(true);
				}
			}
		}
	};

	useEffect(() => {
		if (authStore.user && !hasCheckedAws && !authStore.isLoadingUserOrganizations) {
			setHasCheckedAws(true);
			checkAwsSource();
		}
	}, [authStore.user, authStore.isLoadingUserOrganizations]);

	return (
		<>
			{/* The Confirmation Popup for AWS/Plan checks */}
			<ConfirmationPopup
				open={showAwsPlanConfirmationModal}
				onClose={handleAwsPlanConfirmationConfirm}
				title={getAwsPlanConfirmationMessage()}
				confirmButtonText={t("close")}
				hideCancel={true}
				onClick={handleAwsPlanConfirmationConfirm}
				loading={authStore.isLoading}
			/>
			<SelectOrganizationPopup open={showSelectOrgModal} onClose={handleSelectOrg} />
			<LoadingPopup open={loadingAwsAuth} paperClassName={styles.loadingPopup} />
		</>
	);
};

export default observer(AwsChecker);
